import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-display/DataTable';
import { Badge } from '@/components/data-display/Badge';
import { usePermission } from '@/hooks/usePermission';
import { CREATE_OPERATOR, operatorStatus, type Operator } from '@/features/operators/types';
import { Button } from '@/components/common/Button';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { api } from '@/lib/api-client';

export interface OperatorTenantPermission {
  tenantCod: string;
  permissionCode: string;
}

export type OperatorListItem = Operator;

/** Returns the distinct tenant codes the operator has at least one permission on. */
export function distinctTenants(permissions: OperatorTenantPermission[]): string[] {
  return Array.from(new Set(permissions.map((p) => p.tenantCod))).sort();
}

function formatLastLogin(iso: string | null): string {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

/**
 * Operator-management list page (D-08, plan §8.2).
 *
 * Reads /api/users (enriched in D-05 with the per-operator permission set)
 * and renders one row per operator with a tenant-badge column derived from
 * the permission list. Row click navigates to the detail page; the header
 * CTA navigates to the create form.
 */
export function OperatorListPage() {
  const { has } = usePermission();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery<OperatorListItem[]>({
    queryKey: ['operators'],
    queryFn: () => api.get<OperatorListItem[]>('/api/users'),
  });

  const columns = useMemo<ColumnDef<OperatorListItem, unknown>[]>(
    () => [
      {
        accessorKey: 'username',
        header: 'Username',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="font-semibold text-[#333]">{row.original.username}</span>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableSorting: true,
        cell: ({ row }) => <span className="text-[13px] text-[#555]">{row.original.email}</span>,
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        enableSorting: false,
        // Un compte qui attend ses droits ou son invitation n'est pas
        // « inactif » au sens d'une desactivation : il n'a pas encore commence.
        cell: ({ row }) => {
          switch (operatorStatus(row.original)) {
            case 'active':
              return <Badge variant="success">Active</Badge>;
            case 'inactive':
              return <Badge variant="error">Inactive</Badge>;
            case 'invited':
              return <Badge variant="warning">Invited</Badge>;
            case 'pending':
              return <Badge variant="error">Keycloak pending</Badge>;
            default:
              return <Badge variant="warning">Not invited</Badge>;
          }
        },
      },
      {
        id: 'tenants',
        header: 'Tenants',
        enableSorting: false,
        cell: ({ row }) => {
          const tenants = distinctTenants(row.original.permissions);
          if (tenants.length === 0) {
            return <span className="text-[#999]">—</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {tenants.map((cod) => (
                <Badge key={cod} variant="info">
                  {cod}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        accessorKey: 'lastLoginAt',
        header: 'Last login',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-[13px] text-[#666]">
            {formatLastLogin(row.original.lastLoginAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/operators/${row.original.username}`);
            }}
          >
            View
          </Button>
        ),
      },
    ],
    [navigate],
  );

  const operators = data ?? [];

  return (
    <ErrorBoundary>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-[#333]">Operators</h1>
          <Button
            variant="primary"
            disabled={!has(CREATE_OPERATOR)}
            title={has(CREATE_OPERATOR) ? undefined : 'Requires CREATE_OPERATOR on this tenant'}
            onClick={() => navigate('/admin/operators/new')}
          >
            + Create operator
          </Button>
        </div>

        {error && !isLoading && (
          <div
            className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
            role="alert"
          >
            Failed to load operators. Please try again.
          </div>
        )}

        {!isLoading && !error && operators.length === 0 && (
          <div className="rounded-lg border border-[#e0e0e0] bg-white px-4 py-12 text-center text-sm text-[#999]">
            No operators yet. Click "+ Create operator" to add one.
          </div>
        )}

        {(isLoading || operators.length > 0) && (
          <DataTable<OperatorListItem>
            columns={columns}
            data={operators}
            isLoading={isLoading}
            rowClick={(row) => navigate(`/admin/operators/${row.username}`)}
          />
        )}
      </div>
    </ErrorBoundary>
  );
}
